import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import Header from './components/Header'; // Import Header
import Signup from './components/Signup';
import Login from './components/Login';
import Account from './components/Account';
import InviteResponse from './components/InviteResponse';
import PasswordReset from './components/PasswordReset';
import Logout from './components/Logout';
import { api } from './services/api';
import { initAccountAnalytics, trackAccountPageView } from './utils/analytics';
import { getHostKind, getOrigins, getReturnTargetOrigin, getReturnTargetPath, getReturnToParam } from './utils/host';
import { getStoredItem, setStoredItem } from './utils/storage';

const AppContent = () => {
  const { signupComplete } = React.useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const [bootstrapping, setBootstrapping] = useState(true);
  const handoffExchangeStarted = useRef(false);
  const authRoutes = ['/', '/login', '/signup', '/password-reset', '/invite', '/logout'];
  const isAuthRoute = authRoutes.includes(location.pathname);
  const hostKind = getHostKind();
  const { idOrigin } = getOrigins();
  const debugParam = new URLSearchParams(window.location.search).get('debug_hops') === '1';
  const storedDebug = getStoredItem('finbase.debug_hops') === '1';
  const debugHops = debugParam || storedDebug;
  if (debugParam && !storedDebug) {
    setStoredItem('finbase.debug_hops', '1');
  }

  const logHop = (label) => {
    if (!debugHops) return;
    const sanitized = window.location.href
      .replace(/access_token=[^&]+/g, 'access_token=REDACTED')
      .replace(/refresh_token=[^&]+/g, 'refresh_token=REDACTED')
      .replace(/handoff_token=[^&]+/g, 'handoff_token=REDACTED');
    const payload = {
      url: `${label}:${sanitized}`,
      referrer: document.referrer || '',
      portal: hostKind,
    };
    const endpoint = `${idOrigin}/api/id/v1/debug/hop`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
    } else {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  };

  useEffect(() => {
    logHop('before');
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const handoffToken = params.get('handoff_token') || hashParams.get('handoff_token');

    if (hostKind !== 'id' && isAuthRoute && location.pathname !== '/logout' && !handoffToken) {
      const redirectUrl = new URL(`${idOrigin}${location.pathname}${location.search}`);
      if (!redirectUrl.searchParams.get('return_to')) {
        redirectUrl.searchParams.set('return_to', hostKind === 'ops' ? 'ops' : 'account');
      }
      if (debugHops) {
        redirectUrl.searchParams.set('debug_hops', '1');
      }
      logHop('redirect:id');
      window.location.replace(redirectUrl.toString());
      return;
    }
    if (hostKind === 'ops' && location.pathname.startsWith('/account') && !handoffToken) {
      const redirectUrl = new URL(`${window.location.origin}/admin`);
      redirectUrl.search = window.location.search;
      if (debugHops) {
        redirectUrl.searchParams.set('debug_hops', '1');
      }
      logHop('redirect:ops-admin');
      window.location.replace(redirectUrl.toString());
      return;
    }
    if (hostKind === 'id' && location.pathname.startsWith('/account')) {
      const target = getReturnToParam();
      const targetOrigin = getReturnTargetOrigin(target);
      const redirectUrl = new URL(`${targetOrigin}${getReturnTargetPath(target)}${location.search}`);
      if (debugHops) {
        redirectUrl.searchParams.set('debug_hops', '1');
      }
      logHop('redirect:return_to');
      window.location.replace(redirectUrl.toString());
      return;
    }

    if (handoffToken && hostKind !== 'id') {
      if (handoffExchangeStarted.current) return;
      handoffExchangeStarted.current = true;
      window.history.replaceState(null, '', `${location.pathname}${location.search}`);
      api.post('/acct-mgmt/v1/auth/refresh', { refresh_token: handoffToken })
        .then((response) => {
          signupComplete(response.data.access_token, response.data.refresh_token, response.data);
          history.replace(hostKind === 'ops' ? '/admin' : '/account');
          setBootstrapping(false);
          logHop('handoff-consumed');
        })
        .catch(() => {
          handoffExchangeStarted.current = false;
          setBootstrapping(false);
          history.replace('/login');
        });
      return;
    }

    setBootstrapping(false);
    logHop('bootstrapped');
  }, [signupComplete, history, hostKind, idOrigin, isAuthRoute, location.pathname, location.search]);

  useEffect(() => {
    if (hostKind !== 'account' || bootstrapping) return;
    if (initAccountAnalytics()) {
      trackAccountPageView(location.pathname);
      return;
    }
    trackAccountPageView(location.pathname);
  }, [bootstrapping, hostKind, location.pathname]);

  return (
    <div className={`app-shell${isAuthRoute ? ' app-shell-auth' : ''}`}>
      <Header />
      <main className="app-main">
        {bootstrapping ? (
          <div className="d-flex justify-content-center align-items-center flex-grow-1">
            <div className="spinner-border text-primary" role="status" aria-label="Loading" />
          </div>
        ) : (
          <Switch>
            <Route path="/signup" component={Signup} />
            <Route path="/login" component={Login} />
            <Route path="/logout" component={Logout} />
            <Route path="/password-reset" component={PasswordReset} />
            <Route path="/invite" component={InviteResponse} />
            <Route path="/admin" component={Account} />
            <Route path="/account" component={Account} />
            <Route path="/" exact component={Login} />
          </Switch>
        )}
      </main>
      <footer className="app-footer">
        © 2025–{new Date().getFullYear()} Finbase LLC
      </footer>
    </div>
  );
};

const App = () => (
  <Router>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </Router>
);

export default App;
